import { useEffect, useRef } from "react";

import { useUserContext } from "utils";

import { useSoilSightScenariosAccessBase } from "pages/app/scenario-analysis/hooks/useSoilSightScenariosAccess";
import { useGetSoilSightOnboardingSetting, useUpdateSoilSightOnboardingSetting } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingSetting";
import {
  SoilSightOnboardingState,
  hasPassedLandingForFarm,
  isDraftForFarm,
  isGrandfatheredSoilSightUser,
} from "pages/app/scenario-onboarding/utils/onboardingState";

export type SoilSightOnboardingAccess = {
  isLoading: boolean;
  // The scenario-list or settings query failed — onboarding status cannot be trusted.
  isError: boolean;
  // Completed the flow, or a legacy user grandfathered in (project + scenarios predate it).
  isOnboarded: boolean;
  onboardingState: SoilSightOnboardingState | null;
  // True when an in-progress draft exists for the currently active farm.
  hasDraftForActiveFarm: boolean;
  hasProject: boolean;
  hasScenarios: boolean;
  // True once the active farm's flow has passed the landing (see hasPassedLandingForFarm).
  hasPassedLanding: boolean;
  /**
   * May render/redirect to /scenarios: onboarded with a project + scenarios, or the check errored
   * for a project owner with no draft — a fail-safe, since sending a possibly-grandfathered user
   * through onboarding would overwrite their existing project.
   */
  isAllowed: boolean;
};

export const useSoilSightOnboardingAccess = (): SoilSightOnboardingAccess => {
  const { user } = useUserContext();
  const base = useSoilSightScenariosAccessBase();
  const { onboardingState, isLoading: isSettingLoading, isError: isSettingError } = useGetSoilSightOnboardingSetting();
  const { mutate: persistOnboardingState } = useUpdateSoilSightOnboardingSetting();
  const hasWrittenGrandfatherState = useRef(false);

  const isLoading = isSettingLoading || (base.hasProject && base.isLoading);
  const isError = base.isError || isSettingError;

  // Never grandfather (or stamp "completed") from errored state: a failed settings query
  // makes a mid-draft user's key read as absent, and stamping would overwrite their draft.
  const isGrandfathered =
    !isLoading && !isError && isGrandfatheredSoilSightUser({ hasProject: base.hasProject, hasScenarios: base.hasScenarios, onboardingState });

  // Legacy users were already using /scenarios — stamp them as completed once so future
  // sessions don't depend on re-deriving the scenario list.
  useEffect(() => {
    if (isGrandfathered && !hasWrittenGrandfatherState.current) {
      hasWrittenGrandfatherState.current = true;
      persistOnboardingState({ status: "completed" });
    }
  }, [isGrandfathered, persistOnboardingState]);

  const isOnboarded = onboardingState?.status === "completed" || isGrandfathered;
  // Fail-safe: an errored check for a project owner with no in-progress draft may be a
  // grandfathered legacy user — prefer the non-destructive scenario page over onboarding.
  const isUnverifiedProjectOwner = isError && base.hasProject && onboardingState?.status !== "in-progress";

  return {
    isLoading,
    isError,
    isOnboarded,
    onboardingState,
    hasDraftForActiveFarm: isDraftForFarm(onboardingState, user?.farm?.id),
    hasProject: base.hasProject,
    hasScenarios: base.hasScenarios,
    hasPassedLanding: hasPassedLandingForFarm(onboardingState, user?.farm?.id),
    isAllowed: (isOnboarded && base.hasProject && base.hasScenarios) || isUnverifiedProjectOwner,
  };
};
